import React from 'react';
import type { CreateUserRequest } from '@/api/handlers/resources/users/requests';
import { useCreateUserMutation } from '@/api/queries';
import type { CreateOutcome } from '@/utils';
import { getRoleFields } from './components/teamMeta';
import type { UserFormSubmit } from './components/userFormModal/useUserForm';

/**
 * Creating a user — the one definition, shared by the team page and the
 * Control Center's "Add user".
 *
 * See `@/pages/brands/useCreateBrand` for why the create path lives in a hook
 * of its own. This is the one of the six where that matters beyond tidiness:
 * the body is **built from the role matrix**, not copied from the form. A
 * Seller has no email or password and an OrgAdmin has no branch, and sending a
 * field the role does not have is a 400 the user cannot act on. `getRoleFields`
 * is the same matrix the form uses to decide which inputs to show, so the body
 * and the visible fields cannot disagree — which they could, and eventually
 * would, if the launcher assembled its own.
 */
export const useCreateUser = () => {
    const createMutation = useCreateUserMutation();

    const create = React.useCallback(
        async (input: UserFormSubmit): Promise<CreateOutcome> => {
            const fields = getRoleFields(input.role);
            const body: CreateUserRequest = {
                name: input.name,
                role: input.role,
                ...(fields.branch && input.branchId
                    ? { branchId: input.branchId }
                    : {}),
                ...(fields.email && input.email ? { email: input.email } : {}),
                ...(fields.password && input.password
                    ? { password: input.password }
                    : {}),
                ...(fields.pin && input.pin ? { pin: input.pin } : {}),
            };

            const result = await createMutation.mutateAsync(body);

            return result.success
                ? { ok: true }
                : {
                      ok: false,
                      message: result.error.message ?? 'Failed to create user.',
                  };
        },
        [createMutation],
    );

    return { create, isCreating: createMutation.isPending };
};
